import { domainEvents } from '@/services/events/domainEvents'
import type {
  QuizPassedPayload,
  EpisodeCompletedPayload,
  ScenarioCompletedPayload,
  StoryCompletedPayload,
  EscapeRoomCompletedPayload,
  StreakMilestonePayload,
} from './events'
import type { XPRepository } from '@/repositories/XPRepository'
import type { BadgeRepository } from '@/repositories/BadgeRepository'
import type { ResultsRepository } from '@/repositories/ResultsRepository'
import type { CertificateRepository } from '@/repositories/CertificateRepository'
import type { CertificateService } from '@/services/certificate/CertificateService'
import type { AnalyticsSink } from '@/services/analytics/AnalyticsSink'
import type { BadgeDefinition } from '@/domain/badge'
import {
  XP_PER_QUIZ_PASS,
  XP_PER_EPISODE_COMPLETE,
  XP_PER_SCENARIO_COMPLETE,
  XP_PER_STORY_COMPLETE,
  XP_PER_ESCAPE_ROOM_COMPLETE,
  XP_PER_STREAK_MILESTONE,
  FOUNDATIONS_CERTIFICATE_COURSE_NAME,
  FOUNDATIONS_TRACK_EPISODE_IDS,
} from '@/domain/constants'

// Event-driven gamification engine (Document 11 §4, Document 3 §6; extended
// in P4 for episodes/scenarios/stories/escape rooms/streaks — Document 11
// §P4). Reacts to the same in-process domain event bus every other P0-P3
// write path already emits on. UI code never calls this directly; it only
// ever emits domain events, exactly as it does today.
export class GamificationService {
  constructor(
    private xp: XPRepository,
    private badges: BadgeRepository,
    private results: ResultsRepository,
    private certificates: CertificateRepository,
    private certificateService: CertificateService,
    private analytics: AnalyticsSink
  ) {
    domainEvents.on('quiz.passed', async (event) => {
      await this.handleQuizPassed(event.payload as QuizPassedPayload)
    })
    domainEvents.on('episode.completed', async (event) => {
      await this.handleEpisodeCompleted(event.payload as EpisodeCompletedPayload)
    })
    domainEvents.on('scenario.completed', async (event) => {
      await this.handleScenarioCompleted(event.payload as ScenarioCompletedPayload)
    })
    domainEvents.on('story.completed', async (event) => {
      await this.handleStoryCompleted(event.payload as StoryCompletedPayload)
    })
    domainEvents.on('escaperoom.completed', async (event) => {
      await this.handleEscapeRoomCompleted(event.payload as EscapeRoomCompletedPayload)
    })
    domainEvents.on('streak.milestone', async (event) => {
      await this.handleStreakMilestone(event.payload as StreakMilestonePayload)
    })
  }

  // ---- P1: quiz passed -> XP + episode/capstone badges + certificate ----

  private async handleQuizPassed(payload: QuizPassedPayload) {
    await this.awardXP(payload.userId, payload.orgId, XP_PER_QUIZ_PASS, 'quiz_passed', `quiz-pass-${payload.userId}-${payload.episodeId}`)
    await this.evaluateQuizBadges(payload)
    await this.evaluateCertificate(payload)
  }

  private async evaluateQuizBadges(payload: QuizPassedPayload) {
    const [definitions, userResults] = await Promise.all([
      this.badges.listDefinitions(),
      this.results.listForUser(payload.userId),
    ])
    const passedEpisodeIds = new Set(userResults.filter((r) => r.passed).map((r) => r.episodeId))

    for (const def of definitions.filter((d) => d.ruleEvent === 'quiz_passed')) {
      const matchesSingleEpisode = def.ruleThreshold.episodeId === payload.episodeId
      const matchesCapstone =
        !!def.ruleThreshold.requiredEpisodeIds &&
        def.ruleThreshold.requiredEpisodeIds.every((id) => passedEpisodeIds.has(id))
      const matchesCount =
        def.ruleThreshold.minQuizzesPassed !== undefined && passedEpisodeIds.size >= def.ruleThreshold.minQuizzesPassed

      if (matchesSingleEpisode || matchesCapstone || matchesCount) {
        await this.tryAward(def, payload.userId, payload.orgId)
      }
    }
  }

  private async evaluateCertificate(payload: QuizPassedPayload) {
    const userResults = await this.results.listForUser(payload.userId)
    const passedEpisodeIds = new Set(userResults.filter((r) => r.passed).map((r) => r.episodeId))
    const eligible = FOUNDATIONS_TRACK_EPISODE_IDS.every((id) => passedEpisodeIds.has(id))
    if (!eligible) return

    const existing = await this.certificates.listForUser(payload.userId)
    const alreadyIssued = existing.some(
      (c) => c.courseName === FOUNDATIONS_CERTIFICATE_COURSE_NAME && c.status === 'valid'
    )
    if (alreadyIssued) return

    const certificate = await this.certificateService.generate(payload.userId, FOUNDATIONS_CERTIFICATE_COURSE_NAME)
    domainEvents.emit('certificate.issued', { userId: payload.userId, certificateId: certificate.id })
    this.analytics.track('certificate_issued', { certificateId: certificate.id })
  }

  // ---- P4: episode completed -> small XP + "First Mission" ----

  private async handleEpisodeCompleted(payload: EpisodeCompletedPayload) {
    // episode.completed carries no orgId (Document 11 §P0 event shape) —
    // XP/badge records still need one, so null is used, matching how
    // Individual Learners are already represented everywhere else.
    await this.awardXP(payload.userId, null, XP_PER_EPISODE_COMPLETE, 'episode_completed', `episode-complete-${payload.userId}-${payload.episodeId}`)

    const definitions = await this.badges.listDefinitions()
    for (const def of definitions.filter((d) => d.ruleEvent === 'episode_completed' && d.ruleThreshold.anyEpisode)) {
      await this.tryAward(def, payload.userId, null)
    }
  }

  // ---- P4: practice scenario completed -> XP (first time per scenario) + "Scenario Solver" ----

  private async handleScenarioCompleted(payload: ScenarioCompletedPayload) {
    if (!payload.correct) return // only a successfully solved scenario counts, not every attempt
    await this.awardXP(
      payload.userId,
      payload.orgId,
      XP_PER_SCENARIO_COMPLETE,
      'scenario_completed',
      `scenario-${payload.userId}-${payload.scenarioId}`
    )

    const [definitions, entries] = await Promise.all([this.badges.listDefinitions(), this.xp.listForUser(payload.userId)])
    const distinctScenariosCompleted = new Set(
      entries.filter((e) => e.reason === 'scenario_completed').map((e) => e.sourceEventId)
    ).size

    for (const def of definitions.filter((d) => d.ruleEvent === 'scenario_completed')) {
      if (def.ruleThreshold.minScenariosCompleted !== undefined && distinctScenariosCompleted >= def.ruleThreshold.minScenariosCompleted) {
        await this.tryAward(def, payload.userId, payload.orgId)
      }
    }
  }

  // ---- P4: interactive story completed -> XP (first time per story) + "Story Voyager" ----

  private async handleStoryCompleted(payload: StoryCompletedPayload) {
    await this.awardXP(payload.userId, payload.orgId, XP_PER_STORY_COMPLETE, 'story_completed', `story-${payload.userId}-${payload.storyId}`)

    const definitions = await this.badges.listDefinitions()
    for (const def of definitions.filter((d) => d.ruleEvent === 'story_completed')) {
      const matchesStory = def.ruleThreshold.storyId === payload.storyId
      const matchesOutcome = !def.ruleThreshold.requireGoodOutcome || payload.outcome === 'good'
      if (matchesStory && matchesOutcome) {
        await this.tryAward(def, payload.userId, payload.orgId)
      }
    }
  }

  // ---- P4: escape room completed -> XP (first time per room) + "Escape Artist" ----

  private async handleEscapeRoomCompleted(payload: EscapeRoomCompletedPayload) {
    await this.awardXP(
      payload.userId,
      payload.orgId,
      XP_PER_ESCAPE_ROOM_COMPLETE,
      'escape_room_completed',
      `escaperoom-${payload.userId}-${payload.roomId}`
    )

    const definitions = await this.badges.listDefinitions()
    for (const def of definitions.filter((d) => d.ruleEvent === 'escape_room_completed')) {
      if (def.ruleThreshold.escapeRoomId === payload.roomId) {
        await this.tryAward(def, payload.userId, payload.orgId)
      }
    }
  }

  // ---- P4: streak milestone -> bonus XP (once per milestone tier) + "On a Roll" ----

  private async handleStreakMilestone(payload: StreakMilestonePayload) {
    await this.awardXP(
      payload.userId,
      payload.orgId,
      XP_PER_STREAK_MILESTONE,
      'streak_bonus',
      `streak-${payload.userId}-${payload.streakDays}`
    )

    const definitions = await this.badges.listDefinitions()
    for (const def of definitions.filter((d) => d.ruleEvent === 'streak_milestone')) {
      if (def.ruleThreshold.minStreakDays !== undefined && payload.streakDays >= def.ruleThreshold.minStreakDays) {
        await this.tryAward(def, payload.userId, payload.orgId)
      }
    }
  }

  // ---- shared helpers ----

  private async awardXP(
    userId: string,
    orgId: string | null,
    amount: number,
    reason: Parameters<XPRepository['award']>[0]['reason'],
    sourceEventId: string
  ) {
    await this.xp.award({
      id: crypto.randomUUID(),
      userId,
      orgId,
      amount,
      reason,
      sourceEventId,
      createdAt: new Date().toISOString(),
    })
  }

  private async tryAward(def: BadgeDefinition, userId: string, orgId: string | null) {
    const existingAwards = await this.badges.listAwardsForUser(userId)
    if (existingAwards.some((a) => a.badgeKey === def.badgeKey)) return // already earned — award() is also idempotent, this just avoids a redundant write + event

    await this.badges.award({
      id: crypto.randomUUID(),
      userId,
      orgId,
      badgeKey: def.badgeKey,
      awardedAt: new Date().toISOString(),
      sourceEventId: `${def.badgeKey}-${userId}`,
    })
    domainEvents.emit('badge.awarded', { userId, badgeKey: def.badgeKey })
    this.analytics.track('badge_awarded', { badgeKey: def.badgeKey })
  }
}
